import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	FormControlLabel,
	Grid,
	InputAdornment,
	TextField,
	Typography,
	CircularProgress,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import UserRoles from "helpers/roles";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ArrowIcon from "assets/icons/arrowIcon.svg";
import ColourConstants from "helpers/colourConstants";
import { changeDate } from "helpers/date";
import AccordionBox from "components/Layouts/AccordionBox";
import {
	canAddSiteApplications,
	clientOptions,
	shareModelsOptions,
} from "helpers/constants";
import { updateClientDetails } from "services/clients/clientDetailScreen";
import Dropdown from "components/Elements/Dropdown";
import EMICheckbox from "components/Elements/EMICheckbox";

// const debounce = (func, delay) => {
// 	let timer;
// 	return function () {
// 		let self = this;
// 		let args = arguments;
// 		clearTimeout(timer);
// 		timer = setTimeout(() => {
// 			func.apply(self, args);
// 		}, delay);
// 	};
// };

const useStyles = makeStyles()((theme) => ({
	detailContainer: {
		marginTop: 25,
		display: "flex",
		justifyContent: "center",
	},
	expandIcon: {
		transform: "scale(0.8)",
	},
	sectionHeading: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "17px",
	},
	detailAccordion: {
		borderColor: ColourConstants.commonBorder,
		borderStyle: "solid",
		borderWidth: 1,
		width: "100%",
	},
	labelText: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "14px",
	},
	inputText: {
		fontSize: 14,
	},
	required: { color: "#E31212" },
}));

const ClientDetail = ({
	clientId,
	clientData,
	getError,
	loading,
	role,
	adminType,
}) => {
	const { t } = useTranslation();
	const { classes, cx } = useStyles();
	const [clientDetail, setClientDetail] = useState({});
	const [changedState, setChange] = useState({});
	// This state is used to check current state data with pervious
	useEffect(() => {
		const licenseType = clientOptions().find(
			(x) =>
				x.value === (clientData?.licenseType ?? clientData[0]?.licenseType),
		);

		const shareModelType = shareModelsOptions().find(
			(x) =>
				x.value === (clientData?.shareModels ?? clientData[0]?.shareModels),
		);

		// Resolve site-application permission from API boolean
		// API: allowed / not allowed -> boolean (true | false)
		// canAddSiteApplications: frontend permission options
		const canAddSiteAppPermission = canAddSiteApplications().find(
			({ value }) =>
				value ===
				(clientData?.canAddSiteApplications ??
					clientData[0]?.canAddSiteApplications),
		);

		const data = {
			name: clientData?.name ?? clientData[0]?.name,
			licenseType,
			licenses: clientData?.licenses ?? clientData[0]?.licenses,
			registeredBy: clientData?.registeredBy ?? clientData[0]?.registeredBy,
			registeredDate:
				clientData?.registeredDate ?? clientData[0]?.registeredDate,
			shareModels: shareModelType,
			canAddSiteApplications: canAddSiteAppPermission,
			isRegionalPartner:
				clientData?.isRegionalPartner ??
				clientData[0]?.isRegionalPartner ??
				false,
		};

		setClientDetail(data);
		setChange(data);
		// t: option labels are locale-dependent; recompute when the language or a
		// late-arriving bundle changes translations, not only when clientData does.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientData, t]);

	const changeClientDetails = async (path, value) => {
		try {
			const result = await updateClientDetails(clientId, [
				{ op: "replace", path, value },
			]);
			if (result.status) {
				setChange(result.data);
				return true;
			} else {
				throw new Error(result);
			}
		} catch (err) {
			if (err?.response?.data?.detail) {
				getError(err.response.data.detail);
			}
			if (err?.response?.data?.errors?.name) {
				getError(err.response.data.errors.name[0]);
			}
			return false;
		}
	};

	// const debounceDropDown = useCallback(
	// 	debounce((name, val) => {
	// 		// Check previous data with current data and then patch
	// 		if (changedState[`${name}`] !== val) changeClientDetails(name, val);
	// 	}, 1000),
	// 	[changedState]
	// );

	const handleInputChange = (name, value) => {
		setClientDetail((detail) => ({
			...detail,
			[name]: value,
		}));
	};

	// handling license type dropdown input change and call update api function
	const handleShareModelChange = (path, value) => {
		setClientDetail((detail) => ({
			...detail,
			[path]: value,
		}));

		const { value: modelShareValue } = value;
		handleApiCall("shareModels", modelShareValue);
	};

	// OnBlur Company Name and Liscenses count
	const handleApiCall = (name, value) => {
		if (changedState[`${name}`] !== value) changeClientDetails(name, value);
	};

	//  for can add client add site application dropdown
	const handleCanAddSiteApplicationChange = (path, value) => {
		setClientDetail((detail) => ({
			...detail,
			[path]: value,
		}));
		const { value: canAddSiteAppValue } = value;
		handleApiCall(path, canAddSiteAppValue);
	};
	return (
		<AccordionBox
			title={t("title.companyDetails")}
			noExpand={true}
			accordionClass="companyDetail"
		>
			{loading ? (
				<CircularProgress />
			) : (
				<Grid container spacing={5} className="companyDetailGrid">
					<Grid item sm={12}>
						<Typography className={classes.labelText}>
							{t("label.companyName")}
							<span className={classes.required}>*</span>
						</Typography>
						<TextField
							sx={{
								"& .MuiInputBase-input.Mui-disabled": {
									WebkitTextFillColor: "#000000",
								},
							}}
							name="name"
							variant="outlined"
							fullWidth
							InputProps={{
								classes: {
									input: classes.inputText,
								},
							}}
							onChange={(e) => handleInputChange("name", e.target.value)}
							onBlur={(e) => handleApiCall("name", e.target.value)}
							value={clientDetail.name || ""}
						/>
					</Grid>

					<Grid item sm={6}>
						<Typography className={classes.labelText}>
							{t("label.registeredBy")}
							<span className={classes.required}>*</span>
						</Typography>
						<TextField
							sx={{
								"& .MuiInputBase-input.Mui-disabled": {
									WebkitTextFillColor: "#000000",
								},
							}}
							value={clientDetail.registeredBy || ""}
							variant="outlined"
							fullWidth
							InputProps={{
								classes: {
									input: classes.inputText,
								},
								readOnly: true,
							}}
						/>
					</Grid>
					<Grid item sm={6}>
						<Typography className={classes.labelText}>
							{t("label.registrationDate")}
							<span className={classes.required}>*</span>
						</Typography>
						<TextField
							sx={{
								"& .MuiInputBase-input.Mui-disabled": {
									WebkitTextFillColor: "#000000",
								},
							}}
							id="date"
							variant="outlined"
							fullWidth
							type="date-local"
							value={changeDate(clientDetail.registeredDate)}
							InputProps={{
								classes: {
									input: classes.inputText,
								},
								readOnly: true,
								startAdornment: (
									<InputAdornment style={{ marginRight: 10 }}>
										<CalendarTodayOutlinedIcon
											style={{ fontSize: 19, marginTop: "-3px" }}
										/>
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment>
										<img
											alt="Expand icon"
											src={ArrowIcon}
											className={classes.expandIcon}
										/>
									</InputAdornment>
								),
							}}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>

					<Grid item xs={6}>
						<Typography className={classes.labelText}>
							{t("label.modelSharing")}
							<span className={classes.required}>*</span>
						</Typography>
						<Dropdown
							options={shareModelsOptions()}
							selectedValue={clientDetail.shareModels}
							onChange={(value) => {
								handleShareModelChange("shareModels", value);
							}}
							label=""
							required={true}
							disabled={role !== UserRoles.superAdmin}
							width="100%"
						/>
					</Grid>

					{role === UserRoles.superAdmin && (
						<Grid
							item
							sm={6}
							style={{
								display: "flex",
								alignItems: "center",
							}}
						>
							<FormControlLabel
								control={
									<EMICheckbox
										state={clientDetail.isRegionalPartner ?? false}
										changeHandler={async () => {
											const newValue = !clientDetail.isRegionalPartner;
											handleInputChange("isRegionalPartner", newValue);
											const success = await changeClientDetails(
												"isRegionalPartner",
												newValue
											);
											if (!success) {
												handleInputChange(
													"isRegionalPartner",
													!newValue
												);
											}
										}}
									/>
								}
								label={
									<Typography className={classes.labelText}>
										{t("label.regionalPartner")}
									</Typography>
								}
							/>
						</Grid>
					)}

					{(role === UserRoles.clientAdmin || role === UserRoles.superAdmin) &&
						clientDetail.licenseType?.value !== 4 && (
						<Grid item sm={6}>
							<div className={classes.siteContainer}>
								<Typography variant="subtitle2">
									{t("label.canAddSiteApplication")}
									<span className={classes.required}>*</span>
								</Typography>
								<Dropdown
									options={canAddSiteApplications()}
									selectedValue={clientDetail.canAddSiteApplications}
									onChange={(value) => {
										handleCanAddSiteApplicationChange(
											"canAddSiteApplications",
											value,
										);
									}}
									label=""
									required={true}
									disabled={role !== UserRoles.superAdmin}
									width="100%"
								/>
							</div>
						</Grid>
					)}
				</Grid>
			)}
		</AccordionBox>
	);
};

export default ClientDetail;
